/**
 * @module cercaliagl/servers
 */
import { API_DOMAIN, LB_DOMAIN, SUGGEST_DOMAIN, VERSION, } from './globals';
/**
 * @typedef {Object} ObjectJSON
 * @property {string|undefined} id
 * @property {string|undefined} value
 */
/**
 * @typedef {Object} Coord
 * @property {string|undefined} x
 * @property {string|undefined} y
 */
/**
 * @typedef {Object} PostalCode
 * @property {string|number|undefined} id
 * @property {string|undefined} country_id
 */
/**
 * @typedef {Object} UrlCandidatesEntity
 * @property {Array.<ParamEntity>} param
 */
/**
 * @typedef {Object} UrlCommon
 * @property {ParamEntity} param
 */
/**
 * @typedef {Object} ParamEntity
 * @property {string|undefined} name
 * @property {string|undefined} value
 */
/**
 * @typedef {Object} GeEntity
 * @property {string|null|undefined} article
 * @property {ObjectJSON|null|undefined} city
 * @property {Coord|null|undefined} coord
 * @property {ObjectJSON|null|undefined} country
 * @property {string|null|undefined} dist
 * @property {string|null|undefined} frc
 * @property {ObjectJSON|null|undefined} housenumber
 * @property {string|null|undefined} id
 * @property {ObjectJSON|null|undefined} intersection
 * @property {ObjectJSON|null|undefined} km
 * @property {string|null|undefined} kmh
 * @property {string|null|undefined} label
 * @property {GeEntityGeometry|null|undefined} geometry
 * @property {ObjectJSON|null|undefined} municipality
 * @property {ObjectJSON|string|undefined} name
 * @property {string|null|undefined} pos
 * @property {ObjectJSON|PostalCode|null|undefined} postalcode
 * @property {string|null|undefined} prefix
 * @property {ObjectJSON|null|undefined} region
 * @property {string|null|undefined} sname
 * @property {ObjectJSON|string|null|undefined} subcategory_id
 * @property {ObjectJSON|null|undefined} subregion
 * @property {string|null|undefined} type
 * @property {string|null|undefined} zonename
 * @property {string|null|undefined} zonetype
 * @property {string|null|undefined} zonetype
 * @property {string|null|undefined} zonetype
 * @property {string|null|undefined} zonetype
 */
/**
 * @typedef {Object} GeEntityGeometry
 * @property {string} id
 * @property {string|null} substage
 * @property {string} weight
 * @property {GeEntityGeometryWKT} wkt
 */
/**
 * @typedef {Object} GeEntityGeometryWKT
 * @property {string} value
 */
/**
 * @typedef {Object} GeocodingResult
 * @property {GeocodingResultData} cercalia
 */
/**
 * @typedef {Object} GeocodingResultData
 * @property {GeocodingResultDataCandidates|undefined} candidates
 * @property {string} cmd
 * @property {cercaliax.ObjectJSON|undefined} server
 * @property {cercaliax.ObjectJSON|undefined} error
 */
/**
 * @typedef {Object} GeocodingResultDataCandidates
 * @property {Array.<GeocodingResultDataCandidate>} candidate
 * @property {string|undefined} num
 * @property {string|undefined} pos
 * @property {string|undefined} total
 * @property {UrlCandidatesEntity|undefined} urlcandidates
 * @property {UrlCommon|undefined} urlcommon
 */
/**
 * @typedef {Object} GeocodingResultDataCandidate
 */
/**
 * @typedef {Object} GeometryEntityResult
 * @property {GeometryEntityResultData} cercalia
 */
/**
 * @typedef {Object} GeometryEntityResultData
 * @property {string} cmd
 * @property {GeEntity|undefined} ge
 * @property {cercaliax.ObjectJSON|undefined} server
 * @property {cercaliax.ObjectJSON|undefined} error
 * @property {string|undefined} version
 */
/**
 * @typedef {Object} ProximityResult
 * @property {ProximityResultData} cercalia
 */
/**
 * @typedef {Object} ProximityResultData
 * @property {string} cmd
 * @property {ProximityResultDataProximity} proximity
 * @property {ObjectJSON} server
 * @property {ObjectJSON} error
 */
/**
 * @typedef {Object} ProximityResultDataProximity
 * @property {string|null|undefined} center
 * @property {string|null|undefined} num
 * @property {GeListEntity|undefined} gelist
 * @property {PoiListEntity|null|undefined} poilist
 * @property {string|undefined} rad
 * @property {string|undefined} type
 */
/**
 * @typedef {Object} PoiListEntity
 * @property {string|undefined|null} num
 * @property {Array.<PoiEntity>|null|undefined} poi
 */
/**
 * @typedef {Object} GeListEntity
 * @property {GeEntity} ge
 */
/**
 * @typedef {Object} PoisResult
 * @property {PoisResultData} cercalia
 */
/**
 * @typedef {Object} PoisResultData
 * @property {string} cmd
 * @property {PoisResultDataMap} map
 * @property {PoisResultDataProximity} proximity
 * @property {ObjectJSON|undefined} server
 * @property {string|undefined} version
 * @property {ObjectJSON|undefined} error
 */
/**
 * @typedef {Object} PoisResultDataMap
 * @property {PoisResultDataMapGpoicats} gpoicats
 * @property {PoisResultDataMapImg} img
 * @property {PoisResultDataMapLabel} label
 * @property {Object|null} poicats
 * @property {Object|null} shapes
 * @property {string|null} smap
 * @property {string} style
 */
/**
 * @typedef {Object} PoisResultDataMapGpoicats
 * @property {Array.<string>} poicats
 * @property {PoisResultDataPoiList|null} poilist
 */
/**
 * @typedef {Object} PoisResultDataPoiList
 * @property {number|string|undefined} num
 * @property {Array.<PoisResultDataPoi>|undefined} poi
 */
/**
 * @typedef {Object} PoisResultDataPoi
 * @property {string} category_id
 * @property {Coord} coord
 * @property {string} dist
 * @property {GeEntity} ge
 * @property {string} geometry
 * @property {string} id
 * @property {ObjectJSON} info
 * @property {ObjectJSON} name
 * @property {string|number} pos
 * @property {string|null|undefined} subcategory_id
 */
/**
 * @typedef {Object} PoisResultDataMapImg
 * @property {string} center
 * @property {Object|Array.<string>|null} extent
 * @property {string} format
 * @property {string} height
 * @property {string|null} href
 * @property {string} scale
 * @property {string} width
 */
/**
 * @typedef {Object} PoisResultDataMapLabel
 * @property {string} op
 * @property {string} pt
 */
/**
 * @typedef {Object} PoisResultDataProximity
 */
/**
 * @typedef {Object} IsochronesResult
 * @property {IsochronesResultData} cercalia
 */
/**
 * @typedef {Object} IsochronesResultData
 * @property {string} cmd
 * @property {cercaliax.IsochronesResultDataIsochrones} isochrones
 * @property {string|undefined} server
 * @property {string|undefined} version
 * @property {ObjectJSON|undefined} error
 */
/**
 * @typedef {Object} IsochronesResultDataIsochrones
 * @property {string} center
 * @property {string|undefined} inverse
 * @property {IsochronesResultDataIsochrone|undefined} isochrone
 * @property {string} method
 * @property {string|undefined} net
 * @property {string} ocs
 * @property {string} weight
 */
/**
 * @typedef {Object} IsochronesResultDataIsochrone
 * @property {string} level
 * @property {string} value
 */
/**
 * @typedef {Object} PoiEntity
 * @property {string|null|undefined} category_id
 * @property {string|null|undefined} subcategory_id
 * @property {Coord|undefined} coord
 * @property {string|null|undefined} dist
 * @property {string|null|undefined} id
 * @property {ObjectJSON|undefined} name
 * @property {ObjectJSON|undefined} info
 * @property {GeEntity|undefined} ge
 */
/**
 * @typedef {Object} RoutingResult
 * @property {RoutingResultData} cercalia
 */
/**
 * @typedef {Object} RoutingResultData
 * @property {string} cmd
 * @property {RoutingResultGeometry|undefined} geometry
 * @property {RoutingResultRoute} route
 * @property {ObjectJSON} server
 * @property {string|undefined} version
 * @property {ObjectJSON|undefined} error
 */
/**
 * @typedef {Object} RoutingResultGeometry
 * @property {RoutingResultGeometryRoute} route
 */
/**
 * @typedef {Object} RoutingResultRoute
 * @property {string} coste_v1
 * @property {string} coste_v2
 * @property {string} coste_v3
 * @property {string} dest
 * @property {string} dist
 * @property {RoutingResultGetPoiCats} getpoicats
 * @property {string} icost
 * @property {string} id
 * @property {string} iweight
 * @property {string} lang
 * @property {string} mindist
 * @property {string} orig
 * @property {string} realtime
 * @property {string} totaltime
 * @property {string} breaktime
 * @property {RoutingResultStages} stages
 * @property {RoutingResultStopList} stoplist
 * @property {string} time
 * @property {string} weight
 */
/**
 * @typedef {Object} RoutingResultGeometryRoute
 * @property {string} id
 * @property {string|undefined} substage
 * @property {string} weight
 * @property {RoutingResultWKT} wkt
 */
/**
 * @typedef {Object} RoutingResultWKT
 * @property {string} tolerance
 * @property {string} value
 */
/**
 * @typedef {Object} RoutingResultGetPoiCats
 * @property {Array.<string>} poicats
 */
/**
 * @typedef {Object} RoutingResultStages
 * @property {Array.<RoutingResultStage>} stage
 * @property {string} dist
 * @property {RoutingResultWKT} wkt
 */
/**
 * @typedef {Object} RoutingResultStage
 * @property {string} coste_v1
 * @property {string} coste_v2
 * @property {string} coste_v3
 * @property {string} dest
 * @property {string} dest_stop_id
 * @property {string} id
 * @property {string} orig
 * @property {string} orig_stop_id
 * @property {RoutingResultSubstage} sub_stage
 */
/**
 * @typedef {Object} RoutingResultSubstage
 * @property {string} angle
 * @property {string} arealist
 * @property {string} desc
 * @property {string} dist
 * @property {string} dist_sum
 * @property {string} end
 * @property {string} id
 * @property {string} municipios
 * @property {string} name
 * @property {Object} poilist
 * @property {string} start
 * @property {ObjectJSON} time
 * @property {ObjectJSON} time_sum
 * @property {ObjectJSON} view
 */
/**
 * @typedef {Object} RoutingResultStopList
 * @property {Array.<StopEntity>} stop
 */
/**
 * @typedef {Object} StopEntity
 * @property {string} id
 * @property {GeEntity} ge
 * @property {MoEntity} mo
 * @property {ObjectJSON} desc
 */
/**
 * @typedef {Object} MoEntity
 * @property {Coord} coord
 * @property {string|null} id
 */
/**
 * @typedef {Object} AuthResult
 * @property {AuthResultData} cercalia
 */
/**
 * @typedef {Object} AuthResultData
 * @property {string} cmd
 * @property {ObjectJSON} auth
 * @property {ObjectJSON} error
 */
/**
 * @typedef {Object} SolrItem
 * @property {string|undefined} calle_articulo
 * @property {string|undefined} calle_descripcion
 * @property {string|undefined} calle_id
 * @property {string|undefined} calle_nombre
 * @property {string|undefined} calle_tipo
 * @property {string|undefined} categoria_id
 * @property {string|undefined} className
 * @property {string|undefined} codigo_postal
 * @property {string} comunidad_id
 * @property {string} comunidad_nombre
 * @property {string} coord
 * @property {string|undefined} direccion
 * @property {SolrItem|undefined} intersection
 * @property {number|undefined} jarowinkler_dist
 * @property {string|undefined} lang
 * @property {string|undefined} localidad_id
 * @property {string|undefined} localidad_nombre
 * @property {string|undefined} mail
 * @property {string|undefined} municipio_id
 * @property {string|undefined} municipio_nombre
 * @property {string|undefined} pais_id
 * @property {string|undefined} pais_nombre
 * @property {number|undefined} portal
 * @property {number|undefined} portal_max
 * @property {number|undefined} portal_min
 * @property {string|undefined} poi_nombre
 * @property {string} id
 * @property {string} provincia_id
 * @property {string} provincia_nombre
 * @property {string|undefined} tel
 * @property {string} text
 * @property {string} value
 * @property {string|undefined} web
 */
/**
 * @type {Object.<string, Array.<string>|string>}
 * @api
 */
export default {
    auth: "".concat(API_DOMAIN, "/api/auth"),
    proxy: "".concat(API_DOMAIN, "/api/Proxy"),
    servlet: "".concat(API_DOMAIN, "/api/RequestsAPI"),
    dataServlet: "".concat(API_DOMAIN, "/api/ProxyData"),
    plugins: "".concat(API_DOMAIN, "/gl/v").concat(VERSION, "/plugins"),
    solr: "".concat(SUGGEST_DOMAIN, "/suggest/SuggestServlet"),
    buildings3d: "".concat(LB_DOMAIN, "/maps/buildings/{z}/{x}/{y}.vector.pbf"),
    landmarks3d: "".concat(LB_DOMAIN, "/maps/3dlandmarks/{z}/{x}/{y}.vector.pbf"),
    hdflow: "".concat(LB_DOMAIN, "/maps/traffic/{z}/{x}/{y}.vector.pbf"),
    forecast: 'https://cercaliaint.nexusgeografics.com/cercalia_lbs/server?cmd=vectortile&clientid=0123456789&departuredayofweek={departuredayofweek}&departurefrommidnight={departurefrommidnight}&weight=geometry,spfreeflow,sptime,frc&filter=ferry%3D0&tile={z},{x},{y}',
    a9: "".concat(LB_DOMAIN, "/cgi-bin/mapserv?map=/usr/nexus/cercalia2/world_a9_postgis.map&"),
    postalcodes: "".concat(LB_DOMAIN, "/cgi-bin/mapserv?map=/usr/nexus/cercalia2/world_pd_postgis.map&"),
    municipalities: "".concat(LB_DOMAIN, "/cgi-bin/mapserv?map=/usr/nexus/cercalia2/world_a8_postgis.map&"),
    logistics: "".concat(LB_DOMAIN, "/cgi-bin/mapserv?map=/usr/nexus/cercalia2/logistics_wms.map&"),
    pks: "".concat(LB_DOMAIN, "/cgi-bin/mapserv?&map=/usr/nexus/cercalia2/WMS_pks.map"),
    glyphs: "".concat(LB_DOMAIN, "/tileservergl/fonts/{fontstack}/{range}.pbf"),
};
//# sourceMappingURL=servers.js.map